import {
    ColumnDef,
    flexRender,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { getCoreRowModel, getSortedRowModel } from "@tanstack/table-core";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown, Loader2 } from "lucide-react";
import {
    Table,
    TableHead,
    TableHeader,
    TableRow,
} from "../../component/ui/Table";
import { TruncatedTooltipText } from "./TruncatedTooltipText";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import { SCROLLBAR_STYLE } from "../../component/constants/uiConstants";

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    loading?: boolean;
}

export function DataTable<TData, TValue>({
    columns,
    data,
    loading,
}: DataTableProps<TData, TValue>) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [internalLoading, setInternalLoading] = useState(true);
    const tableBodyRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setInternalLoading(false);
    }, [data]);

    const isLoading = loading || internalLoading;

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
        },
    });

    const { rows } = table.getRowModel();

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: () => 40,
        getScrollElement: () => tableBodyRef.current,
        overscan: 10,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    const minRequiredWidth = columns.length * 200;
    const shouldUseFixedWidth =
        minRequiredWidth > (tableRef.current?.offsetWidth || 0);
    const tableWidth = shouldUseFixedWidth ? `${minRequiredWidth}px` : "100%";
    const columnWidth = shouldUseFixedWidth
        ? "200px"
        : `${100 / columns.length}%`;

    if (isLoading) {
        // h-[100%] is necessary; don't know why h-full doesn't work
        return (
            <div className="flex h-[100%]">
                <div className="rounded-[4px] border-[1px] border-[#C6C5C5] dark:border-[#7A8690] h-full flex flex-auto flex-col mx-2 mb-2 mt-0 items-center justify-center">
                    <div className="inset-0 flex items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin stroke-black dark:stroke-white" />
                    </div>
                </div>
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="flex h-[100%]">
                <div className="rounded-[4px] border-[1px] border-[#C6C5C5] dark:border-[#7A8690] h-full flex flex-auto flex-col mx-2 mb-2 mt-0 items-center justify-center">
                    <div className="text-[#535353] dark:text-[#CEE0EF]">
                        No data to display
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[100%] w-full px-2 pb-2 pt-0">
            <style>{SCROLLBAR_STYLE}</style>
            <div
                ref={tableRef}
                className="relative rounded-[4px] border-[1px] border-[#C6C5C5] dark:border-[#7A8690] h-[100%] flex flex-initial flex-col"
            >
                <OverlayScrollbarsComponent
                    defer
                    options={{
                        scrollbars: {
                            autoHide: "leave",
                            autoHideDelay: 300,
                        },
                        overflow: {
                            x: "scroll",
                            y: "hidden",
                        },
                    }}
                    className="w-full h-[100%] flex flex-initial custom-scrollbar"
                >
                    <div className="inline-block min-w-full h-[100%]">
                        <Table
                            className="table-fixed"
                            style={{ width: tableWidth }}
                        >
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow
                                        key={headerGroup.id}
                                        className="border-[#C6C5C5] dark:border-[#7A8690]"
                                    >
                                        {headerGroup.headers.map((header) => (
                                            <TableHead
                                                key={header.id}
                                                className="h-[40px] px-4 bg-white dark:bg-[#2D3748] whitespace-nowrap"
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    width: columnWidth,
                                                    minWidth: columnWidth,
                                                    cursor: header.column.getCanSort()
                                                        ? "pointer"
                                                        : "default",
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="truncate flex-grow">
                                                        <TruncatedTooltipText
                                                            text={
                                                                typeof header
                                                                    .column
                                                                    .columnDef
                                                                    .header ===
                                                                "string"
                                                                    ? header
                                                                          .column
                                                                          .columnDef
                                                                          .header
                                                                    : String(
                                                                          flexRender(
                                                                              header
                                                                                  .column
                                                                                  .columnDef
                                                                                  .header,
                                                                              header.getContext()
                                                                          )
                                                                      )
                                                            }
                                                            className="w-full font-bold"
                                                        >
                                                            {flexRender(
                                                                header.column
                                                                    .columnDef
                                                                    .header,
                                                                header.getContext()
                                                            )}
                                                        </TruncatedTooltipText>
                                                    </div>
                                                    {header.column.getCanSort() && (
                                                        <div className="flex-none">
                                                            {{
                                                                asc: (
                                                                    <ChevronUp className="h-4 w-4" />
                                                                ),
                                                                desc: (
                                                                    <ChevronDown className="h-4 w-4" />
                                                                ),
                                                            }[
                                                                header.column.getIsSorted() as string
                                                            ] ?? (
                                                                <ChevronsUpDown className="h-4 w-4 opacity-50" />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                        </Table>
                        <div
                            ref={tableBodyRef}
                            className="flex-1 overflow-y-auto max-h-[100vh]"
                        >
                            <div className="inline-block min-w-full">
                                <div
                                    style={{
                                        height: `${rowVirtualizer.getTotalSize()}px`,
                                        position: "relative",
                                    }}
                                >
                                    {virtualItems.map((virtualRow) => {
                                        const row = rows[virtualRow.index];
                                        return (
                                            <div
                                                key={row.id}
                                                data-index={virtualRow.index}
                                                ref={
                                                    rowVirtualizer.measureElement
                                                }
                                                className="absolute top-0 left-0 w-full border-b border-[#C6C5C5] dark:border-[#7A8690] hover:bg-[#F9F9F9] dark:hover:bg-[#4A5568] h-[40px] flex"
                                                style={{
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                }}
                                            >
                                                {row
                                                    .getVisibleCells()
                                                    .map((cell) => {
                                                        const rawValue =
                                                            cell.getValue();
                                                        const renderedContent =
                                                            flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext()
                                                            );
                                                        const displayContent =
                                                            typeof rawValue ===
                                                                "string" ||
                                                            typeof rawValue ===
                                                                "number" ? (
                                                                <TruncatedTooltipText
                                                                    text={String(
                                                                        rawValue
                                                                    )}
                                                                    className="w-full"
                                                                >
                                                                    {
                                                                        renderedContent
                                                                    }
                                                                </TruncatedTooltipText>
                                                            ) : (
                                                                renderedContent
                                                            );

                                                        return (
                                                            <div
                                                                key={cell.id}
                                                                className="flex items-center px-4 text-[#535353] dark:text-[#CEE0EF]"
                                                                style={{
                                                                    width: columnWidth,
                                                                    minWidth:
                                                                        columnWidth,
                                                                }}
                                                            >
                                                                <div className="truncate w-full">
                                                                    {
                                                                        displayContent
                                                                    }
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </OverlayScrollbarsComponent>
            </div>
        </div>
    );
}
